import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Bar, ResponsiveContainer, Label } from "recharts";
import {
  Typography,
  Paper,
  Stack,
  useTheme,
  Box,
  Autocomplete,
  TextField,
  Chip,
  Tooltip as MuiTooltip,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { readAsyncStorageValues } from "features/service/service.slice";
import {
  getMeanTimeBetweenFailure,
  getMeanTimeToRepair,
  getTotalMeanTimeBetweenFailure,
  getTotalMeanTimeToRepair,
  getMachines,
} from "features/service/service.slice";

const MAX_MACHINE_SELECTION = 15;
const ALL_MACHINES_OPTION = { id: "all", name: "All Machines" };
const DEFAULT_MTBF_MTTR_HOURS_PER_DAY = 22;

const formatMtbfDays = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, useGrouping: false }).format(n);
};

const formatMttrHours = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, useGrouping: false }).format(n);
};

const parseServiceTicketCount = (apiResponse) => {
  const n = Number(apiResponse?.serviceTicketCount);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const renderChartTooltip =
  (isPerMachineView, axisLabelStroke) =>
  ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    const activeEntry = payload.find((item) => item.value != null && Number.isFinite(Number(item.value)));
    if (!activeEntry) return null;

    const row = activeEntry.payload;
    const isMtbf = activeEntry.dataKey === "mtbfDays";
    const metricLabel = row?.metric || (isMtbf ? "MTBF" : "MTTR");
    const valueText = isMtbf
      ? `${formatMtbfDays(activeEntry.value)} days`
      : `${formatMttrHours(activeEntry.value)} hours`;
    const title = isPerMachineView ? `${row?.fullName || row?.name || ""} (${metricLabel})` : row?.name || metricLabel;
    const ticketCount = parseServiceTicketCount(row);

    return (
      <Paper elevation={4} sx={{ p: 1.25, minWidth: 160 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: axisLabelStroke }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: axisLabelStroke, mt: 0.5 }}>
          {valueText}
        </Typography>
        <Typography variant="body2" sx={{ color: axisLabelStroke, mt: 0.25 }}>
          Tickets: {ticketCount}
        </Typography>
      </Paper>
    );
  };

const MTBFvsMTTR = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { machinesData, appConstants } = useSelector((state) => state.service);
  const [machineList, setMachineList] = useState([]);
  const [machineIdsList, setMachineIdsList] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [machineSearchText, setMachineSearchText] = useState("");
  const [isMachineAutocompleteFocused, setIsMachineAutocompleteFocused] = useState(false);
  const { mode } = useSelector((state) => state.globalState);
  const [chartData, setChartData] = useState([
    { name: "MTBF (Left Axis)", mtbfDays: 0 },
    { name: "MTTR (Right Axis)", mttrHours: 0 },
  ]);

  const mtbfHoursPerDay = useMemo(() => {
    const n = Number(appConstants?.mtbfMttrWorkingHoursPerDay);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_MTBF_MTTR_HOURS_PER_DAY;
  }, [appConstants?.mtbfMttrWorkingHoursPerDay]);

  const hoursToMtbfDays = useCallback(
    (hours) => {
      const h = Number(hours);
      if (!Number.isFinite(h)) return 0;
      return h / mtbfHoursPerDay;
    },
    [mtbfHoursPerDay],
  );

  useEffect(() => {
    dispatch(getMachines({ breakdownOnly: true }));
    dispatch(readAsyncStorageValues());
  }, [dispatch]);

  useEffect(() => {
    if (machinesData) {
      const modifiedMachineData = machinesData
        .map((item) => ({
          ...item,
          id: item.id,
          name: `${item.maintenanceCode || ""} - ${item.manufacturer}, ${item.model} [${item.serialNumber}]`,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setMachineList([ALL_MACHINES_OPTION, ...modifiedMachineData]);
      setMachineIdsList(modifiedMachineData.map((item) => item.id));
    }
  }, [machinesData]);

  const selectedMachineIds = selectedMachines.map((m) => m.id).filter(Boolean);
  const selectedMachinesRef = useRef(selectedMachines);
  selectedMachinesRef.current = selectedMachines;

  const onChangeMachine = useCallback(
    async ({ machineIdsToUse, selectedMachineMeta }) => {
      // Summary mode: nothing selected => show totals for all machines (current behavior).
      if (!machineIdsToUse?.length) {
        const mtbf = await dispatch(getTotalMeanTimeBetweenFailure({ machineList: machineIdsList })).unwrap();
        const mttr = await dispatch(getTotalMeanTimeToRepair({ machineList: machineIdsList })).unwrap();

        const mtbfH = Number(mtbf.getMeanTimeBetweenFailure);
        const mttrH = Number(mttr.totalMeanTimeToRepair);
        setChartData([
          {
            name: "MTBF (Left Axis)",
            mtbfDays: Number.isFinite(mtbfH) ? hoursToMtbfDays(mtbfH) : 0,
            serviceTicketCount: parseServiceTicketCount(mtbf),
          },
          {
            name: "MTTR (Right Axis)",
            mttrHours: Number.isFinite(mttrH) ? mttrH : 0,
            serviceTicketCount: parseServiceTicketCount(mttr),
          },
        ]);
        return;
      }

      // Per-machine mode: selected machines => show MTBF machine bars on the left,
      // and MTTR machine bars on the right (no totals).
      {
        const metaMap = new Map((selectedMachineMeta || []).map((m) => [m.id, m]));
        const safeNameForId = (id) => {
          const fromMeta = metaMap.get(id)?.name;
          const fromList = machineList.find((m) => m.id === id)?.name;
          return fromMeta || fromList || String(id);
        };
        const safeMaintenanceCodeForId = (id) => {
          const fromMeta = metaMap.get(id)?.maintenanceCode;
          const fromList = machineList.find((m) => m.id === id)?.maintenanceCode;
          return fromMeta || fromList || String(id);
        };

        const perMachineRows = await Promise.all(
          machineIdsToUse.map(async (id) => {
            const [mtbfRes, mttrRes] = await Promise.all([
              dispatch(getMeanTimeBetweenFailure(id)).unwrap(),
              dispatch(getMeanTimeToRepair(id)).unwrap(),
            ]);

            const mtbfH = Number(mtbfRes.meanTimeBetweenFailuresInHours);
            const mttrH = Number(mttrRes.meanRepairTimeInHours);
            return {
              id,
              maintenanceCode: safeMaintenanceCodeForId(id),
              fullName: safeNameForId(id),
              mtbfDays: Number.isFinite(mtbfH) ? hoursToMtbfDays(mtbfH) : 0,
              mttrHours: Number.isFinite(mttrH) ? mttrH : 0,
              mtbfTicketCount: parseServiceTicketCount(mtbfRes),
              mttrTicketCount: parseServiceTicketCount(mttrRes),
            };
          }),
        );

        // Build two blocks so MTBF appears on the left and MTTR on the right.
        const mtbfBlock = perMachineRows.map((r) => ({
          id: `${r.id}-mtbf`,
          name: `${r.maintenanceCode}`,
          fullName: r.fullName,
          metric: "MTBF",
          mtbfDays: r.mtbfDays,
          serviceTicketCount: r.mtbfTicketCount,
        }));
        const mttrBlock = perMachineRows.map((r) => ({
          id: `${r.id}-mttr`,
          name: `${r.maintenanceCode}`,
          fullName: r.fullName,
          metric: "MTTR",
          mttrHours: r.mttrHours,
          serviceTicketCount: r.mttrTicketCount,
        }));

        setChartData([...mtbfBlock, ...mttrBlock]);
      }
    },
    [dispatch, machineIdsList, machineList, hoursToMtbfDays],
  );

  const handleClearAllSelections = () => {
    setSelectedMachines([]);
    onChangeMachine({ machineIdsToUse: [] });
  };

  useEffect(() => {
    if (machineIdsList.length > 0) {
      const currentSelection = selectedMachinesRef.current;
      onChangeMachine({
        machineIdsToUse: currentSelection.map((m) => m.id).filter(Boolean),
        selectedMachineMeta: currentSelection.length ? currentSelection : undefined,
      });
    }
  }, [machineIdsList, mtbfHoursPerDay, onChangeMachine]);

  const axisLabelStroke = mode === "dark" ? "#fff" : "#000";
  const mtbfFill = theme.palette.primary.main;
  const mttrFill = theme.palette.secondary[300];

  const renderMtbfBarValueLabel = (props) => {
    const { x, y, width, value } = props;
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return null;

    return (
      <text x={x + width / 2} y={y - 6} textAnchor="middle" fill={axisLabelStroke} fontWeight={700} fontSize={12}>
        {formatMtbfDays(n)}
      </text>
    );
  };

  const renderMttrBarValueLabel = (props) => {
    const { x, y, width, value } = props;
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return null;

    return (
      <text x={x + width / 2} y={y - 6} textAnchor="middle" fill={axisLabelStroke} fontWeight={700} fontSize={12}>
        {formatMttrHours(n)}
      </text>
    );
  };

  const isPerMachineView = selectedMachines.length > 0;
  const barSize = isPerMachineView ? Math.max(8, Math.min(24, Math.round(240 / (selectedMachines.length * 2)))) : 30;

  const showAllMachinesText = selectedMachines.length === 0 && (machineSearchText || "").length === 0 && !isMachineAutocompleteFocused;
  const machineAutocompleteInputValue = showAllMachinesText ? "All Machines" : machineSearchText;

  return (
    <Stack alignItems={"center"} sx={{ mt: 5 }}>
      <Box width={"100%"}>
        <Paper elevation={8} sx={{ padding: 1, mx: 3, minWidth: 600 }}>
          <Typography variant="h4" align="center" sx={{ mb: 4, mt: 2 }}>
            Mean Time Between Failure & Mean Time To Repair
          </Typography>

          <Stack flexDirection="column" alignItems="center" sx={{ mb: 3 }} gap={1.5}>
            <Autocomplete
              multiple
              disablePortal
              id="machine-list"
              disableClearable
              getOptionLabel={(option) => option?.name || ""}
              options={machineList.filter((item) => item.name)}
              value={selectedMachines}
              inputValue={machineAutocompleteInputValue}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "reset") return;
                // Prevent the synthetic "All Machines" display text from becoming the user's search query.
                if (newInputValue === "All Machines") return;
                setMachineSearchText(newInputValue);
              }}
              onChange={(event, newValue, reason) => {
                // Keep selections when user only clears the search text via the clear (X) icon.
                if (reason === "clear") {
                  setMachineSearchText("");
                  return;
                }
                const picked = newValue || [];
                if (picked.some((m) => m?.id === "all")) {
                  setSelectedMachines([]);
                  setMachineSearchText("");
                  onChangeMachine({ machineIdsToUse: [] });
                  return;
                }
                const trimmed = picked.slice(0, MAX_MACHINE_SELECTION);
                setSelectedMachines(trimmed);
                onChangeMachine({ machineIdsToUse: trimmed.map((m) => m.id).filter(Boolean), selectedMachineMeta: trimmed });
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              filterSelectedOptions
              getOptionDisabled={(option) => {
                if (option?.id === "all") return false;
                return selectedMachines.length >= MAX_MACHINE_SELECTION && !selectedMachineIds.includes(option.id);
              }}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Select Machine(s)`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        <IconButton
                          aria-label="Clear search"
                          size="small"
                          onClick={() => setMachineSearchText("")}
                          disabled={(machineSearchText || "").length === 0}
                          sx={{
                            visibility: "visible",
                            mr: 0.5,
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  inputProps={{
                    ...params.inputProps,
                    onFocus: (e) => {
                      setIsMachineAutocompleteFocused(true);
                      params.inputProps?.onFocus?.(e);
                    },
                    onBlur: (e) => {
                      setIsMachineAutocompleteFocused(false);
                      params.inputProps?.onBlur?.(e);
                    },
                    onKeyDown: (e) => {
                      // Prevent MUI's default behavior where Backspace/Delete removes the last selected option
                      // when the input is empty. Clearing/typing should not change selections.
                      if (
                        (e.key === "Backspace" || e.key === "Delete") &&
                        (machineSearchText || "").length === 0 &&
                        selectedMachines.length > 0
                      ) {
                        e.stopPropagation();
                      }

                      params.inputProps?.onKeyDown?.(e);
                    },
                  }}
                  helperText={
                    selectedMachines.length >= MAX_MACHINE_SELECTION
                      ? `Maximum ${MAX_MACHINE_SELECTION} machines selected. Remove one to add another.`
                      : "Tip: Select one machine for individual MTBF/MTTR, or multiple to see totals for that subset."
                  }
                />
              )}
              sx={{ minWidth: 600, maxWidth: 900 }}
            />

            <Paper
              variant="outlined"
              sx={{
                width: "100%",
                maxWidth: 900,
                p: 1.5,
                borderColor: mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)",
                backgroundColor: mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }} gap={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Selected machines ({selectedMachines.length}/{MAX_MACHINE_SELECTION})
                </Typography>
                <Button variant="outlined" size="small" onClick={handleClearAllSelections} disabled={selectedMachines.length === 0}>
                  Clear all
                </Button>
              </Stack>

              {selectedMachines.length === 0 ? (
                <Typography variant="body2" sx={{ color: axisLabelStroke }}>
                  None selected (showing all machines).
                </Typography>
              ) : (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {selectedMachines.map((m) => {
                    const full = m?.name || "";
                    const short = full.length > 10 ? `${full.slice(0, 10)}…` : full;
                    return (
                      <MuiTooltip key={m.id} title={full} arrow>
                        <Chip
                          label={short}
                          onDelete={() => {
                            const next = selectedMachines.filter((x) => x.id !== m.id);
                            setSelectedMachines(next);
                            onChangeMachine({ machineIdsToUse: next.map((mm) => mm.id).filter(Boolean), selectedMachineMeta: next });
                          }}
                          size="small"
                          variant="outlined"
                        />
                      </MuiTooltip>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Stack>

          <Box
            sx={{
              width: "100%",
              mx: "auto",
              px: { xs: 0.5, sm: 1 },
            }}
          >
            <ResponsiveContainer width="100%" height={isPerMachineView ? 420 : 360}>
              <ComposedChart
                data={chartData}
                margin={{ top: 28, right: 32, left: 32, bottom: isPerMachineView ? 90 : 28 }}
                barCategoryGap={isPerMachineView ? "15%" : "25%"}
              >
                <CartesianGrid yAxisId="mtbf" stroke="#d6d6d6" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: axisLabelStroke, fontWeight: 700 }}
                  interval={isPerMachineView ? 0 : undefined}
                  angle={isPerMachineView ? -30 : 0}
                  textAnchor={isPerMachineView ? "end" : "middle"}
                  height={isPerMachineView ? 90 : undefined}
                  tickMargin={4}
                  dy={isPerMachineView ? 10 : 0}
                />
                <YAxis yAxisId="mtbf" orientation="left" width={80} tick={{ fill: axisLabelStroke }} tickMargin={10}>
                  <Label
                    value="MTBF (days)"
                    angle={-90}
                    position="left"
                    offset={4}
                    fill={axisLabelStroke}
                    style={{ textAnchor: "middle", fontWeight: 700 }}
                  />
                </YAxis>
                <YAxis yAxisId="mttr" orientation="right" width={80} tick={{ fill: axisLabelStroke }} tickMargin={10}>
                  <Label
                    value="MTTR (hours)"
                    angle={90}
                    position="right"
                    offset={4}
                    fill={axisLabelStroke}
                    style={{ textAnchor: "middle", fontWeight: 700 }}
                  />
                </YAxis>
                <Tooltip content={renderChartTooltip(isPerMachineView, axisLabelStroke)} />
                <Bar
                  yAxisId="mtbf"
                  dataKey="mtbfDays"
                  name="mtbfDays"
                  fill={mtbfFill}
                  barSize={barSize}
                  isAnimationActive={false}
                  label={renderMtbfBarValueLabel}
                />
                <Bar
                  yAxisId="mttr"
                  dataKey="mttrHours"
                  name="mttrHours"
                  fill={mttrFill}
                  barSize={barSize}
                  isAnimationActive={false}
                  label={renderMttrBarValueLabel}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>
    </Stack>
  );
};

export default MTBFvsMTTR;
